/**
 * @sazeo/schemas — CRM Leads
 *
 * Zod schemas for the CRM-Leads addon (migration 0012 tables). These are
 * the wire shapes the tenant-app SDK and the Edge Functions agree on.
 *
 * Spec: specs/CODEX_PHASE4_WAVE2_CRM_INVOICING.md §2.3
 */

import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Row shapes
// =============================================================================

export const CrmPipelineSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  name: z.string().min(1),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type CrmPipeline = z.infer<typeof CrmPipelineSchema>;

export const CrmStageSchema = z.object({
  id: UuidSchema,
  pipeline_id: UuidSchema,
  tenant_id: UuidSchema,
  name: z.string().min(1),
  position: z.number().int().nonnegative(),
  win_probability: z.number().min(0).max(100).nullable(),
  is_won: z.boolean(),
  is_lost: z.boolean(),
  created_at: TimestampSchema,
});
export type CrmStage = z.infer<typeof CrmStageSchema>;

export const CrmLeadSourceSchema = z.enum(['inbound', 'referral', 'outbound', 'other']);
export type CrmLeadSource = z.infer<typeof CrmLeadSourceSchema>;

export const CrmLeadSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  pipeline_id: UuidSchema,
  stage_id: UuidSchema,
  owner_id: UuidSchema.nullable(),
  title: z.string().min(1),
  company_name: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_email: z.string().email().nullable(),
  contact_phone: z.string().nullable(),
  value_usd: z.number().nullable(),
  source: z.string().nullable(),
  description: z.string().nullable(),
  source_lang: LangCodeSchema.or(z.string()),
  closed_at: TimestampSchema.nullable(),
  won_at: TimestampSchema.nullable(),
  lost_reason: z.string().nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type CrmLead = z.infer<typeof CrmLeadSchema>;

export const CrmLeadActivityKindSchema = z.enum([
  'note',
  'call',
  'email',
  'meeting',
  'stage_change',
]);
export type CrmLeadActivityKind = z.infer<typeof CrmLeadActivityKindSchema>;

export const CrmLeadActivitySchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  lead_id: UuidSchema,
  actor_id: UuidSchema,
  kind: CrmLeadActivityKindSchema,
  body: z.string().nullable(),
  source_lang: LangCodeSchema.or(z.string()),
  metadata: z.record(z.string(), z.unknown()),
  created_at: TimestampSchema,
});
export type CrmLeadActivity = z.infer<typeof CrmLeadActivitySchema>;

// =============================================================================
// Input shapes
// =============================================================================

export const CrmLeadCreateInputSchema = z.object({
  pipeline_id: UuidSchema,
  stage_id: UuidSchema,
  owner_id: UuidSchema.nullable().optional(),
  title: z.string().min(1).max(200),
  company_name: z.string().max(200).nullable().optional(),
  contact_name: z.string().max(200).nullable().optional(),
  contact_email: z.string().email().nullable().optional(),
  contact_phone: z.string().max(40).nullable().optional(),
  value_usd: z.number().nonnegative().nullable().optional(),
  source: z.string().max(40).nullable().optional(),
  description: z.string().max(4000).nullable().optional(),
  source_lang: z.string().min(2).max(8).optional(),
});
export type CrmLeadCreateInput = z.infer<typeof CrmLeadCreateInputSchema>;

export const CrmLeadUpdateInputSchema = z
  .object({
    title: z.string().min(1).max(200),
    owner_id: UuidSchema.nullable(),
    company_name: z.string().max(200).nullable(),
    contact_name: z.string().max(200).nullable(),
    contact_email: z.string().email().nullable(),
    contact_phone: z.string().max(40).nullable(),
    value_usd: z.number().nonnegative().nullable(),
    source: z.string().max(40).nullable(),
    description: z.string().max(4000).nullable(),
    lost_reason: z.string().max(400).nullable(),
  })
  .partial();
export type CrmLeadUpdateInput = z.infer<typeof CrmLeadUpdateInputSchema>;

export const CrmLeadTransitionInputSchema = z.object({
  stage_id: UuidSchema,
  note: z.string().max(1000).optional(),
});
export type CrmLeadTransitionInput = z.infer<typeof CrmLeadTransitionInputSchema>;

export const CrmLeadActivityCreateInputSchema = z.object({
  kind: z.enum(['note', 'call', 'email', 'meeting']),
  body: z.string().max(4000).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  source_lang: z.string().min(2).max(8).optional(),
});
export type CrmLeadActivityCreateInput = z.infer<typeof CrmLeadActivityCreateInputSchema>;

// =============================================================================
// Filter shapes
// =============================================================================

export const CrmLeadFilterSchema = z
  .object({
    pipeline_id: UuidSchema.optional(),
    stage_id: UuidSchema.optional(),
    owner_id: UuidSchema.optional(),
    only_open: z.boolean().optional(),
    search: z.string().optional(),
    limit: z.number().int().positive().max(200).optional(),
  })
  .partial();
export type CrmLeadFilter = z.infer<typeof CrmLeadFilterSchema>;
